
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';
import styles from '../../core-exchange.module.scss';
export const metadata = {
  toc: true,
  subnav: true,
  title: 'App2App',
  layout: 'guide',
  description: 'Enable seamless mobile authentication for users who have your app installed',
  preloadImages: [{
    src: '/assets/img/core-exchange/app2app-example-flow.gif',
    type: 'image/gif'
  }],
  parentTocLevel: 2,
  childTocLevel: 3,
  secondLevelToc: true,
  alwaysExpand: true
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Image = makeShortcode("Image");
const Callout = makeShortcode("Callout");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <div className={styles.page}>
      <Header title='App2App' subtitle={metadata.description} mdxType="Header" />
      <h2 {...{
        "id": "is-app2app-right-for-you"
      }}>{`Is App2App right for you?`}</h2>
      <p><strong parentName="p">{`Implement App2App if:`}</strong></p>
      <ul>
        <li parentName="ul">{`You have a mobile banking app (iOS or Android)`}</li>
        <li parentName="ul">{`Users frequently link accounts from their phones`}</li>
        <li parentName="ul">{`You want to reduce authentication friction`}</li>
        <li parentName="ul">{`You can support deep linking and in-app OAuth flows`}</li>
      </ul>
      <p><strong parentName="p">{`Impact:`}</strong>{` App2App improves mobile conversion rates up to 15% through biometric login and seamless user flow.`}</p>
      <Image src='/assets/img/core-exchange/app2app-example-flow.gif' alt='App2App example flow showing biometric authentication' mdxType="Image" />
      <h2 {...{
        "id": "how-app2app-works"
      }}>{`How App2App works`}</h2>
      <p>{`App2App makes mobile authentication faster and smoother. If your users already have your mobile app installed, they can authenticate there instead of using a mobile browser.`}</p>
      <p><strong parentName="p">{`The App2App flow:`}</strong></p>
      <ol>
        <li parentName="ol">{`User selects your institution in Plaid Link`}</li>
        <li parentName="ol">{`Plaid detects App2App support and checks for your app on the device`}</li>
        <li parentName="ol">{`If installed, Plaid redirects to your deep link URL`}</li>
        <li parentName="ol">{`Your mobile app opens and parses the OAuth parameters from the deep link`}</li>
        <li parentName="ol">{`User authenticates in your app (using biometrics, credentials, or existing session)`}</li>
        <li parentName="ol">{`Your app generates an authorization code and redirects back to Plaid`}</li>
        <li parentName="ol">{`Plaid completes the linking and returns the user to the originating app`}</li>
      </ol>
      <h2 {...{
        "id": "set-up-deep-link-urls"
      }}>{`Set up deep link URLs`}</h2>
      <p>{`Configure HTTPS URLs that launch your app when users click them (if they have it installed). Fall back to the mobile web page if the app's not there (or if they're on desktop).`}</p>
      <p>{`Platform-specific guides:`}</p>
      <ul>
        <li parentName="ul"><strong parentName="li">{`iOS:`}</strong>{` `}<a parentName="li" {...{
            "href": "https://developer.apple.com/documentation/xcode/allowing-apps-and-websites-to-link-to-your-content?language=objc"
          }}>{`Universal links`}</a></li>
        <li parentName="ul"><strong parentName="li">{`Android:`}</strong>{` `}<a parentName="li" {...{
            "href": "https://developer.android.com/training/app-links#android-app-links"
          }}>{`App Links`}</a></li>
      </ul>
      <Callout warning mdxType="Callout">
        <p>{`Use only HTTPS URLs, not custom schemes (such as `}<inlineCode parentName="p">{`bankapp://`}</inlineCode>{`). HTTPS ensures compatibility with both app and browser fallback.`}</p>
      </Callout>
      <h2 {...{
        "id": "build-the-in-app-authentication-flow"
      }}>{`Build the in-app authentication flow`}</h2>
      <p>{`Your mobile app needs to handle OAuth authorization natively.`}</p>
      <h3 {...{
        "id": "implementation-approaches"
      }}>{`Implementation approaches`}</h3>
      <p><strong parentName="p">{`Native authentication (recommended):`}</strong>{` Users authenticate fully inside your app. After authentication, generate the authorization code and redirect back to Plaid.`}</p>
      <p><strong parentName="p">{`Embedded web view (alternative):`}</strong>{` Start authentication in-app, then complete it via embedded web view. Some UX trade-offs, but simpler implementation.`}</p>
      <h3 {...{
        "id": "parse-oauth-parameters-from-the-deep-link"
      }}>{`Parse OAuth parameters from the deep link`}</h3>
      <p>{`When Plaid launches your app, the deep link includes standard OAuth parameters. Your app must parse these:`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-bash",
          "metastring": "# Example deep link",
          "#": true,
          "Example": true,
          "deep": true,
          "link": true
        }}>{`https://yourbank.com/mobile_app?
  response_type=code
  &client_id=plaid-client-id
  &redirect_uri=https%3A%2F%2Fcdn.plaid.com%2Flink%2Fv2%2Fstable%2Foauth.html
  &state=oauth-state-id
  &scope=openid%20offline_access
`}</code></pre>
      <p><strong parentName="p">{`Required parameters:`}</strong></p>
      <table>
        <thead parentName="table">
          <tr parentName="thead">
            <th parentName="tr" {...{
              "align": null
            }}>{`Parameter`}</th>
            <th parentName="tr" {...{
              "align": null
            }}>{`Description`}</th>
          </tr>
        </thead>
        <tbody parentName="table">
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`response_type`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Always `}<inlineCode parentName="td">{`code`}</inlineCode></td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`client_id`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`The client ID you issued to Plaid`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`redirect_uri`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Where to send the authorization code (Plaid's OAuth handler)`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`state`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Opaque state value to include in your redirect`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`scope`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Requested scopes (e.g., `}<inlineCode parentName="td">{`openid offline_access`}</inlineCode>{`)`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`code_challenge`}</inlineCode>{` / `}<inlineCode parentName="td">{`code_challenge_method`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Optional, if you're using PKCE`}</td>
          </tr>
        </tbody>
      </table>
      <h3 {...{
        "id": "redirect-back-to-plaid"
      }}>{`Redirect back to Plaid`}</h3>
      <p>{`After successful authentication, redirect the user to Plaid with the authorization code:`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-bash",
          "metastring": "# Example redirect",
          "#": true,
          "Example": true,
          "redirect": true
        }}>{`https://cdn.plaid.com/link/v2/stable/oauth.html?code=AUTH_CODE&state=STATE_ID
`}</code></pre>
      <p>{`Include the `}<inlineCode parentName="p">{`state`}</inlineCode>{` parameter exactly as received. Plaid will validate it and complete the OAuth flow.`}</p>
      <p>{`If your mobile authentication flow includes MFA, the user must complete it in your app before you redirect. MFA cannot happen after the redirect back to Plaid.`}</p>
      <p>{`If the user resumes an earlier deep link session (through biometrics or an existing app session), recognize the `}<inlineCode parentName="p">{`state`}</inlineCode>{` value you were given rather than prompting for credentials again. This keeps the flow smooth for users who are already authenticated.`}</p>
      <h2 {...{
        "id": "provide-urls-to-plaid"
      }}>{`Provide URLs to Plaid`}</h2>
      <p>{`Provide two URLs to your Plaid contact:`}</p>
      <ul>
        <li parentName="ul"><strong parentName="li">{`App2App deep link URL`}</strong>{` - For native mobile flow (e.g., `}<inlineCode parentName="li">{`https://yourbank.com/mobile_app`}</inlineCode>{`)`}</li>
        <li parentName="ul"><strong parentName="li">{`Web authorization endpoint`}</strong>{` - From your `}<inlineCode parentName="li">{`.well-known/openid-configuration`}</inlineCode>{` (fallback for browser)`}</li>
      </ul>
      <p>{`This allows Plaid to control the user path and isolate any issues that may arise (for example, if a specific app incorrectly implements the App2App flow).`}</p>
      <h2 {...{
        "id": "handle-fallback-to-the-browser"
      }}>{`Handle fallback to the browser`}</h2>
      <p>{`If the user doesn't have your app installed, the OS opens the deep link in a browser instead. Detect these browser-based deep link requests on your server and redirect them to your standard OAuth authorization endpoint (as specified in the `}<inlineCode parentName="p">{`.well-known/openid-configuration`}</inlineCode>{` endpoint).`}</p>
      <p>{`The OS controls fallback routing. Plaid doesn't decide whether to use the app or the browser. Make sure your deep link URL is publicly accessible so browser fallback works.`}</p>
      <h2 {...{
        "id": "implementation-checklist"
      }}>{`Implementation checklist`}</h2>
      <ul>
        <li parentName="ul">{`Deep link URLs configured (iOS Universal Links and/or Android App Links)`}</li>
        <li parentName="ul">{`In-app OAuth authentication flow built`}</li>
        <li parentName="ul">{`Authorization code generation working in-app`}</li>
        <li parentName="ul">{`Redirect back to Plaid working correctly`}</li>
        <li parentName="ul">{`MFA, if any, completed in-app before the redirect`}</li>
        <li parentName="ul">{`Fallback to web flow tested`}</li>
        <li parentName="ul">{`Both redirect URLs provided to Plaid`}</li>
        <li parentName="ul">{`Testing complete on iOS and Android`}</li>
      </ul>
      <p>{`Once these pieces are in place, contact your Plaid representative. Plaid coordinates App2App testing with you to confirm the implementation is ready for production.`}</p>
    </div>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "is-app2app-right-for-you",
        level: 2,
        title: "Is App2App right for you?",
      },
{
        id: "how-app2app-works",
        level: 2,
        title: "How App2App works",
      },
{
        id: "set-up-deep-link-urls",
        level: 2,
        title: "Set up deep link URLs",
      },
{
        id: "build-the-in-app-authentication-flow",
        level: 2,
        title: "Build the in-app authentication flow",
      },
{
        id: "implementation-approaches",
        level: 3,
        title: "Implementation approaches",
      },
{
        id: "parse-oauth-parameters-from-the-deep-link",
        level: 3,
        title: "Parse OAuth parameters from the deep link",
      },
{
        id: "redirect-back-to-plaid",
        level: 3,
        title: "Redirect back to Plaid",
      },
{
        id: "provide-urls-to-plaid",
        level: 2,
        title: "Provide URLs to Plaid",
      },
{
        id: "handle-fallback-to-the-browser",
        level: 2,
        title: "Handle fallback to the browser",
      },
{
        id: "implementation-checklist",
        level: 2,
        title: "Implementation checklist",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
